<template>
  <el-card class="main-card outbound-tracking-page">
    <template #header>
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">货品管理</el-breadcrumb-item>
        <el-breadcrumb-item>外发货品跟踪</el-breadcrumb-item>
      </el-breadcrumb>
      <operation-help namespace="stock" help-key="outbound-tracking" />
    </template>

    <stock-bound-tabs />

    <el-alert class="status-tip" type="warning" :closable="false" show-icon
      title="本页仅跟踪 2026-01-01 及之后符合当前流程专用单号前缀的正式外发数据：维修 DHH-WX、融金 DHH-RJ、送料加工 DHH-SLJG、借货 JH；其他前缀及更早历史数据不纳入。" />

    <el-alert class="status-tip" type="info" :closable="false" show-icon
      title="本页仅依据正式出入库、临时明细和库存单据展示业务完结与库存入账状态，不再计算专项流程的批次进度、差异或逾期状态。" />

    <el-form :model="filters" label-width="100px" class="search-form">
      <el-row>
        <el-form-item label="关键词">
          <el-input v-model="filters.keyword" clearable prefix-icon="Search" style="width:190px"
            placeholder="单号/款号/批次号" @keyup.enter="search" />
        </el-form-item>
        <el-form-item v-if="showOrgFilters" label="所属公司">
          <el-tree-select v-model="filters.companyId" :data="companys" clearable check-strictly
            :render-after-expand="false" style="min-width:220px" @change="search" />
        </el-form-item>
        <el-form-item label="业务类型">
          <el-select v-model="filters.businessType" clearable style="width:135px" @change="search">
            <el-option label="维修" value="repair" />
            <el-option label="借货" value="borrow" />
            <el-option label="送料加工" value="tolling" />
            <el-option label="融金" value="gold" />
          </el-select>
        </el-form-item>
        <el-form-item label="结案方式">
          <el-select v-model="filters.completionMode" clearable style="width:135px" @change="search">
            <el-option label="回货入库" value="return" />
            <el-option label="回款结案" value="cash" />
          </el-select>
        </el-form-item>
      </el-row>
      <el-row>
        <el-form-item label="批次号">
          <el-input v-model="filters.serialCode" clearable style="width:160px" placeholder="精确查询"
            @keyup.enter="search" />
        </el-form-item>
        <el-form-item label="系统款号">
          <el-input v-model="filters.detailCode" clearable style="width:160px" placeholder="精确查询"
            @keyup.enter="search" />
        </el-form-item>
        <el-form-item label="品牌款号">
          <el-input v-model="filters.brandModelCode" clearable style="width:160px" placeholder="精确查询"
            @keyup.enter="search" />
        </el-form-item>
      </el-row>
      <el-row>
        <el-form-item label="完结状态">
          <el-select v-model="filters.completionStatus" clearable placeholder="全部" style="width:145px" @change="search">
            <el-option label="出库未完结" value="open" />
            <el-option label="已完结" value="closed" />
          </el-select>
        </el-form-item>
        <el-form-item label="库存状态">
          <el-select v-model="filters.inventoryStatus" clearable placeholder="全部" style="width:155px" @change="search">
            <el-option v-for="item in inventoryOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="正式出库日">
          <el-date-picker v-model="outboundRange" type="daterange" unlink-panels start-placeholder="开始日期"
            end-placeholder="结束日期" :shortcuts="$util.getShortCuts()" @change="search" />
        </el-form-item>
        <el-form-item label="" label-width="30px">
          <el-button type="primary" icon="Search" @click="search">搜索</el-button>
          <el-button icon="RefreshLeft" @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-row>
    </el-form>

    <div class="toolbar">
      <el-button type="primary" plain icon="Download" :loading="exportLoading" @click="exportRows">导出跟踪明细</el-button>
    </div>

    <basic-table class="table" :query="query" @handler="tableHandler">
      <template #businessType="scope">
        <el-tag size="small" :type="businessTagType(scope.row.businessType)">{{ scope.row.businessTypeText }}</el-tag>
      </template>
      <template #trackingScope="scope">
        <el-tooltip v-if="scope.row.trackingScope === 'bill'" content="该业务无法将出库货品与回货/回款逐行对应，数量进度按整单展示">
          <el-tag size="small" type="info">{{ scope.row.trackingScopeText }}</el-tag>
        </el-tooltip>
        <el-tag v-else size="small">{{ scope.row.trackingScopeText }}</el-tag>
      </template>
      <template #companyID="scope">{{ companyName(scope.row.companyID) }}</template>
      <template #sourceBillCode="scope">
        <el-link type="primary" :underline="false" @click="openBusiness(scope.row)">{{ scope.row.sourceBillCode }}</el-link>
      </template>
      <template #inboundBillCodes="scope">
        <span>{{ scope.row.inboundBillCodes || '—' }}</span>
      </template>
      <template #completedQuantity="scope">{{ quantityText(scope.row.completedQuantity) }}</template>
      <template #processingQuantity="scope">{{ quantityText(scope.row.processingQuantity) }}</template>
      <template #pendingQuantity="scope">{{ quantityText(scope.row.pendingQuantity) }}</template>
      <template #completionStatus="scope">
        <el-tag size="small" :type="scope.row.completionStatus === 'closed' ? 'success' : 'warning'">
          {{ scope.row.completionStatusText }}
        </el-tag>
      </template>
      <template #inventoryStatus="scope">
        <el-tag size="small" :type="inventoryTagType(scope.row.inventoryStatus)">{{ scope.row.inventoryStatusText }}</el-tag>
      </template>
      <template #outboundDate="scope">{{ dateTimeText(scope.row.outboundDate) }}</template>
      <template #actualCompletionDate="scope">{{ dateTimeText(scope.row.actualCompletionDate) }}</template>
      <template #toolbar="scope">
        <el-link type="primary" @click="openBusiness(scope.row)">业务单</el-link>
        <el-link type="success" class="row-action" @click="openOutbound(scope.row)">出库流水</el-link>
        <el-link v-if="scope.row.inboundBillCodes" type="warning" class="row-action" @click="openInbound(scope.row)">入库单</el-link>
      </template>
    </basic-table>
  </el-card>
</template>

<script>
import BasicTable from "@/components/basic-table.vue";
import StockBoundTabs from "@/views/stock/bound/StockBoundTabs.vue";
import { fetchOutboundTracking, exportOutboundTracking } from "@/api/stock";

const defaultFilters = () => ({
  keyword: "",
  serialCode: "",
  detailCode: "",
  brandModelCode: "",
  companyId: "",
  businessType: "",
  completionMode: "",
  completionStatus: "open",
  inventoryStatus: ""
});

export default {
  name: "OutboundTracking",
  components: { BasicTable, StockBoundTabs },
  data() {
    return {
      filters: defaultFilters(),
      routeCompanyPlaceholder: "",
      outboundRange: [],
      companys: [],
      exportLoading: false,
      inventoryOptions: [
        { label: "未回库", value: "not_returned" },
        { label: "回货处理中", value: "return_review" },
        { label: "已回货待入库", value: "pending_inbound" },
        { label: "部分已入库", value: "partial_inbound" },
        { label: "已全部入库", value: "inbound" },
        { label: "无需入库", value: "not_applicable" }
      ],
      query: {
        page: 1,
        rows: 10,
        records: 0,
        total: 0,
        data: [],
        sidx: "outboundDate",
        sord: "desc",
        columns: [
          { type: "index", label: "序号" },
          { type: "template", prop: "businessType", label: "业务", width: "90", sortable: true },
          { type: "template", prop: "trackingScope", label: "口径", width: "100" },
          { prop: "completionModeText", label: "结案方式", width: "100", showOverflowTooltip: true },
          { type: "template", prop: "companyID", label: "公司", width: "130", showOverflowTooltip: true },
          { type: "template", prop: "sourceBillCode", label: "原出库单号", width: "155", sortable: true, showOverflowTooltip: true },
          { prop: "returnBillCode", label: "待回/归还单号", width: "165", showOverflowTooltip: true },
          { type: "template", prop: "inboundBillCodes", label: "入库单号", width: "175", showOverflowTooltip: true },
          { prop: "detailCode", label: "系统款号", width: "125", sortable: true, showOverflowTooltip: true },
          { prop: "detailName", label: "货品名称", width: "160", showOverflowTooltip: true },
          { prop: "serialCode", label: "批次号", width: "120", sortable: true, showOverflowTooltip: true },
          { prop: "brandSerialCode", label: "品牌批次号", width: "120", showOverflowTooltip: true },
          { prop: "brandModelCode", label: "品牌款号", width: "120", showOverflowTooltip: true },
          { prop: "unitName", label: "规格", width: "80" },
          { prop: "storeCode", label: "原库房", width: "90" },
          { prop: "channel", label: "原渠道", width: "90" },
          { prop: "outboundQuantity", label: "出库数量", width: "95", sortable: true, align: "right" },
          { type: "template", prop: "completedQuantity", label: "已完成", width: "85", align: "right" },
          { type: "template", prop: "processingQuantity", label: "审核中", width: "85", align: "right" },
          { type: "template", prop: "pendingQuantity", label: "待回", width: "85", align: "right" },
          { type: "template", prop: "completionStatus", label: "业务完结", width: "110" },
          { type: "template", prop: "inventoryStatus", label: "库存状态", width: "120" },
          { type: "template", prop: "outboundDate", label: "出库时间", width: "145", sortable: true },
          { type: "template", prop: "actualCompletionDate", label: "实际业务完成", width: "150", sortable: true },
          { prop: "daysOutside", label: "在外天数", width: "95", sortable: true, align: "right" },
          { prop: "operatorName", label: "经办人", width: "95", showOverflowTooltip: true },
          { prop: "remark", label: "备注", width: "180", showOverflowTooltip: true },
          { type: "toolbar", label: "操作", width: "185" }
        ]
      }
    };
  },
  created() {
    this.applyRouteFilters();
  },
  mounted() {
    this.$kaung.initCompanys(this, this.initializeCompanies, { companyIdKey: "routeCompanyPlaceholder" });
  },
  methods: {
    initializeCompanies() {
      if (!this.filters.companyId && this.routeCompanyPlaceholder !== "*") {
        this.filters.companyId = this.routeCompanyPlaceholder || "";
      }
      this.fetchRows();
    },
    applyRouteFilters() {
      const route = this.$route.query || {};
      if (route.keyword) this.filters.keyword = route.keyword;
      if (route.serialCode) this.filters.serialCode = route.serialCode;
      if (route.detailCode) this.filters.detailCode = route.detailCode;
      if (route.brandModelCode) this.filters.brandModelCode = route.brandModelCode;
      if (route.businessType) this.filters.businessType = route.businessType;
      if (route.companyId) this.filters.companyId = route.companyId;
    },
    requestParams() {
      const endOfDay = value => value ? this.$util.fmtDate(value, "yyyy-MM-dd") + " 23:59:59" : null;
      return {
        page: this.query.page,
        rows: this.query.rows,
        sidx: this.query.sidx,
        sord: this.query.sord,
        ...this.filters,
        companyId: this.filters.companyId === "*" ? "" : this.filters.companyId,
        outboundStartDate: this.outboundRange?.[0] ? this.$util.fmtDate(this.outboundRange[0], "yyyy-MM-dd") : null,
        outboundEndDate: endOfDay(this.outboundRange?.[1])
      };
    },
    fetchRows() {
      fetchOutboundTracking(this.requestParams()).then(res => {
        this.query = { ...this.query, ...(res.data || {}), columns: this.query.columns };
      });
    },
    search() {
      this.query.page = 1;
      this.fetchRows();
    },
    resetFilters() {
      const defaultCompany = this.showOrgFilters ? (this.$store.getters.getUserInfo?.companyID || "") : "";
      this.filters = { ...defaultFilters(), companyId: defaultCompany };
      this.outboundRange = [];
      this.search();
    },
    tableHandler(payload) {
      this.query = { ...this.query, ...(payload || {}) };
      this.fetchRows();
    },
    exportRows() {
      this.exportLoading = true;
      exportOutboundTracking(this.requestParams()).then(res => {
        this.$notify({ title: "导出成功", message: res.content, type: "success", duration: 10000 });
        window.location.href = process.env.VUE_APP_BASEURL + res.data;
      }).catch(error => {
        this.$message.error(error?.content || error?.message || "导出失败");
      }).finally(() => { this.exportLoading = false; });
    },
    quantityText(value) {
      return value === null || typeof value === "undefined" ? "—" : value;
    },
    dateTimeText(value) {
      return value ? String(value).replace("T", " ").split(".")[0] : "—";
    },
    businessTagType(type) {
      return type === "gold" ? "warning" : type === "tolling" ? "success" : type === "borrow" ? "danger" : "primary";
    },
    inventoryTagType(status) {
      if (status === "inbound") return "success";
      if (["pending_inbound", "partial_inbound", "return_review"].includes(status)) return "warning";
      return "info";
    },
    companyName(companyId) {
      const match = this.findCompany(companyId, this.companys);
      return match?.label || match?.companyName || match?.name || companyId || "";
    },
    findCompany(companyId, list) {
      if (!companyId || !Array.isArray(list)) return null;
      for (const item of list) {
        if (item.id === companyId || item.ID === companyId || item.value === companyId) return item;
        const child = this.findCompany(companyId, item.children || item.Children);
        if (child) return child;
      }
      return null;
    },
    openBusiness(row) {
      if (row.businessType === "borrow") {
        this.$router.push({ path: "/stock/bills/borrowDetail", query: { billCode: row.sourceBillCode } });
        return;
      }
      this.$router.push({
        path: "/stock/bills/pendingReturnDetail",
        query: { id: row.returnBillID || row.returnBillCode || row.sourceBillID, template: row.businessType }
      });
    },
    openOutbound(row) {
      this.$router.push({ path: "/stock/bills/detail", query: { billCode: row.sourceBillCode } });
    },
    openInbound(row) {
      const billCode = String(row.inboundBillCodes || "").split(",")[0];
      if (billCode) this.$router.push({ path: "/stock/bills/indetail", query: { billCode } });
    }
  },
  watch: {
    "$route.query": {
      deep: true,
      handler() {
        this.applyRouteFilters();
        this.search();
      }
    }
  }
};
</script>

<style scoped>
.outbound-tracking-page :deep(.el-card__body) {
  padding-top: 0;
}

.status-tip {
  margin-bottom: 16px;
}

.row-action {
  margin-left: 8px;
}
</style>
